import { IconClose } from "@/assets/icons/geist/IconClose";
import { type WorkflowEditFormTriggerRepositoryListContext } from "@/components/automations/automation-edit-form-data";
import { Button } from "@/components/flexkit/Button";
import { Input } from "@/components/podkit/forms/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { Text } from "@/components/podkit/typography/Text";
import { CornerDownLeft } from "lucide-react";
import {
    parseCSVData,
    extractUrlsFromColumn,
    isValidRepositoryInput,
    detectRepositoryColumn,
    type CSVData,
    type CSVParseOptions,
} from "@/utils/csv-import";
import { toast } from "@/components/podkit/toasts/use-toast";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import type React from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { ChevronDown, ChevronUp, Circle } from "lucide-react";
import { MAX_REPO_URLS } from "@/components/automations/AutomationEditCodeContext";
import { useListRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import { useSearchRepositoriesPaginated, type PlainSearchRepository } from "@/queries/runner-queries";
import { SearchMode } from "gitpod-next-api/gitpod/v1/runner_pb";
import { ErrorMessage } from "@/components/ErrorMessage";
import { getScmDisplayName } from "@/components/automations/scm-utils";
import { withGitpodSize } from "@/assets/icons/utils";

import { Pill } from "@/components/podkit/status/Pill";

export const RepositoryListContextContent: FC<{
    context: WorkflowEditFormTriggerRepositoryListContext;
    runnerId: string;
    disabled: boolean;
    onAddUrls: (urls: string[], clearPendingMigration?: boolean) => void;
    onRemoveUrl: (url: string) => void;
    onClearAll: () => void;
}> = ({ context, runnerId, disabled, onAddUrls, onRemoveUrl, onClearAll }) => {
    return (
        <RepositoryListContent
            repoUrls={context.repoUrls}
            runnerId={runnerId}
            disabled={disabled}
            onAddUrls={onAddUrls}
            onRemoveUrl={onRemoveUrl}
            onClearAll={onClearAll}
        />
    );
};

const RepositoryListSearchImport: FC<{
    runnerId: string;
    disabled: boolean;
    existingUrls: string[];
    onImport: (urls: string[]) => void;
}> = ({ runnerId, disabled, existingUrls, onImport }) => {
    const [searchString, setSearchString] = useState("");
    const [scmHost, setScmHost] = useState("");
    const [searchResults, setSearchResults] = useState<string[]>([]);
    const [hasSearched, setHasSearched] = useState(false);

    const handleSearchComplete = useCallback((repos: { url: string }[]) => {
        const urls = repos.map((r) => r.url);
        setSearchResults(urls);
        setHasSearched(true);
    }, []);

    const handleImportAll = useCallback(() => {
        // Filter out URLs that already exist
        const newUrls = searchResults.filter((url) => !existingUrls.includes(url));
        if (newUrls.length === 0) {
            toast({
                title: "No new repositories",
                description: "All found repositories are already in the list.",
            });
            return;
        }
        onImport(newUrls);
    }, [searchResults, existingUrls, onImport]);

    // Count how many URLs are new (not in existingUrls)
    const newCount = searchResults.filter((url) => !existingUrls.includes(url)).length;

    return (
        <div className="flex flex-col gap-3">
            <RepoFilteringContent
                disabled={disabled}
                runnerId={runnerId}
                repoSearchString={searchString}
                scmHost={scmHost}
                onScmHostChange={setScmHost}
                onAddFilter={setSearchString}
                onClearFilter={() => {
                    setSearchString("");
                    setSearchResults([]);
                    setHasSearched(false);
                }}
                onSearchComplete={handleSearchComplete}
            />
            {hasSearched && searchResults.length > 0 && (
                <div className="flex flex-row justify-end">
                    <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={handleImportAll}
                        disabled={newCount === 0}
                        data-tracking-id="import-search-results"
                    >
                        <span>
                            Add {newCount} {newCount === 1 ? <span>repository</span> : <span>repositories</span>}
                        </span>
                    </Button>
                </div>
            )}
        </div>
    );
};

type ImportState = { mode: "idle" } | { mode: "search" } | { mode: "csv-preview"; data: CSVData; fileContent: string };

const SEPARATOR_OPTIONS = [
    { value: ",", label: "Comma (,)" },
    { value: ";", label: "Semicolon (;)" },
    { value: "\t", label: "Tab" },
    { value: "|", label: "Pipe (|)" },
];

const RepositoryListContent: FC<{
    repoUrls: string[];
    runnerId: string;
    disabled: boolean;
    onAddUrls: (urls: string[], clearPendingMigration?: boolean) => void;
    onRemoveUrl: (url: string) => void;
    onClearAll: () => void;
}> = ({ repoUrls, runnerId, disabled, onAddUrls, onRemoveUrl, onClearAll }) => {
    const [inputValue, setInputValue] = useState("");
    const [validationError, setValidationError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Import state
    const [importState, setImportState] = useState<ImportState>({ mode: "idle" });
    const [separator, setSeparator] = useState(",");
    const [hasHeaders, setHasHeaders] = useState(true);
    const [selectedColumn, setSelectedColumn] = useState(0);

    // Import dropdown state
    const [importDropdownOpen, setImportDropdownOpen] = useState(false);

    const handleAddUrl = useCallback(() => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;

        if (repoUrls.length >= MAX_REPO_URLS) {
            setValidationError(`Maximum of ${MAX_REPO_URLS} repositories allowed.`);
            return;
        }

        if (!isValidRepositoryInput(trimmed)) {
            setValidationError(
                "Please enter a full URL (e.g., https://github.com/owner/repo or github.com/owner/repo)",
            );
            return;
        }

        setValidationError(null);
        onAddUrls([trimmed]);
        setInputValue("");
    }, [inputValue, repoUrls.length, onAddUrls]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
        setValidationError(null);
    }, []);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") {
                event.preventDefault();
                handleAddUrl();
            }
        },
        [handleAddUrl],
    );

    const handleImportFromSearch = useCallback(() => {
        setImportDropdownOpen(false);
        setImportState({ mode: "search" });
    }, []);

    const handleImportFromCSV = useCallback(() => {
        setImportDropdownOpen(false);
        // Immediately open file selector
        fileInputRef.current?.click();
    }, []);

    const handleCancelImport = useCallback(() => {
        setImportState({ mode: "idle" });
    }, []);

    const handleFileChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target?.result as string;
                const options: CSVParseOptions = { separator, hasHeaders };
                const data = parseCSVData(content, options);

                if (data.rows.length === 0) {
                    toast({
                        title: "No data found",
                        description: "The file appears to be empty.",
                    });
                    return;
                }

                // Auto-detect the best column for repository URLs
                const detectedColumn = detectRepositoryColumn(data);
                setSelectedColumn(detectedColumn);
                setImportState({ mode: "csv-preview", data, fileContent: content });
            };
            reader.onerror = () => {
                toast({
                    title: "Failed to read file",
                    description: "There was an error reading the CSV file. Please try again.",
                });
            };
            reader.readAsText(file);

            // Reset the input so the same file can be selected again
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        },
        [separator, hasHeaders],
    );

    const handleReparse = useCallback(
        (newSeparator: string, newHasHeaders: boolean) => {
            if (importState.mode === "csv-preview") {
                const options: CSVParseOptions = { separator: newSeparator, hasHeaders: newHasHeaders };
                const data = parseCSVData(importState.fileContent, options);
                // Re-detect the best column after reparsing
                const detectedColumn = detectRepositoryColumn(data);
                setSelectedColumn(detectedColumn);
                setImportState({ mode: "csv-preview", data, fileContent: importState.fileContent });
            }
        },
        [importState],
    );

    const handleConfirmCSVImport = useCallback(() => {
        if (importState.mode !== "csv-preview") return;

        const result = extractUrlsFromColumn(importState.data, selectedColumn);

        if (result.validUrls.length === 0) {
            toast({
                title: "No valid repositories found",
                description:
                    result.skippedCount > 0
                        ? `${result.skippedCount} entries were skipped. URLs must include the host (e.g., github.com/owner/repo).`
                        : "The selected column contains no data.",
            });
            return;
        }

        // Check limit
        const availableSlots = MAX_REPO_URLS - repoUrls.length;
        if (availableSlots <= 0) {
            toast({
                title: "Limit reached",
                description: `Maximum of ${MAX_REPO_URLS} repositories allowed. Remove some repositories first.`,
            });
            return;
        }

        const urlsToImport = result.validUrls.slice(0, availableSlots);
        const truncatedCount = result.validUrls.length - urlsToImport.length;

        onAddUrls(urlsToImport);

        // Show success feedback with details
        let description = "";
        if (truncatedCount > 0) {
            description = `${truncatedCount} URLs were not imported (limit of ${MAX_REPO_URLS} reached).`;
        } else if (result.skippedCount > 0) {
            description = `${result.skippedCount} entries were skipped (invalid format or missing host).`;
        } else {
            description = "All entries were successfully imported.";
        }

        toast({
            title: `Imported ${urlsToImport.length} repositories`,
            description,
        });

        // Reset import state
        setImportState({ mode: "idle" });
    }, [importState, selectedColumn, repoUrls.length, onAddUrls]);

    const handleSearchResultsAdd = useCallback(
        (urls: string[]) => {
            const availableSlots = MAX_REPO_URLS - repoUrls.length;
            if (availableSlots <= 0) {
                toast({
                    title: "Limit reached",
                    description: `Maximum of ${MAX_REPO_URLS} repositories allowed.`,
                });
                return;
            }

            const urlsToAdd = urls.slice(0, availableSlots);
            onAddUrls(urlsToAdd);

            if (urlsToAdd.length < urls.length) {
                toast({
                    title: `Added ${urlsToAdd.length} repositories`,
                    description: `${urls.length - urlsToAdd.length} were not added (limit of ${MAX_REPO_URLS} reached).`,
                });
            } else {
                toast({
                    title: `Added ${urlsToAdd.length} repositories`,
                });
            }

            setImportState({ mode: "idle" });
        },
        [repoUrls.length, onAddUrls],
    );

    if (!runnerId) {
        return (
            <div className="flex min-h-[46px] w-full items-center rounded-lg border border-border-subtle px-3">
                <Text className="flex h-[30px] items-center text-base text-content-muted">
                    Select an environment class to add repositories.
                </Text>
            </div>
        );
    }

    // Hidden file input for CSV import
    const fileInput = (
        <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            onChange={handleFileChange}
            className="hidden"
            disabled={disabled}
        />
    );

    // Render the appropriate input area based on import state
    const renderInputArea = () => {
        switch (importState.mode) {
            case "search":
                return runnerId ? (
                    <div className="flex flex-col gap-3 rounded-lg border border-border-base p-3">
                        <div className="flex flex-row items-center justify-between">
                            <Text className="text-base font-normal">Import from Search</Text>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={handleCancelImport}
                                data-tracking-id="cancel-search-import"
                            >
                                Cancel
                            </Button>
                        </div>
                        <RepositoryListSearchImport
                            runnerId={runnerId}
                            disabled={disabled}
                            existingUrls={repoUrls}
                            onImport={handleSearchResultsAdd}
                        />
                    </div>
                ) : null;

            case "csv-preview":
                return (
                    <div className="flex flex-col overflow-hidden rounded-lg border border-border-base">
                        {/* Header */}
                        <div className="flex flex-row items-center justify-between border-b border-border-base bg-surface-base px-3 py-2">
                            <Text className="text-base font-normal">
                                Select the column that contains your repository URLs
                            </Text>
                            <div className="flex flex-row items-center gap-2">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleCancelImport}
                                    data-tracking-id="cancel-csv-import"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    variant="primary"
                                    size="sm"
                                    onClick={handleConfirmCSVImport}
                                    data-tracking-id="confirm-csv-import"
                                >
                                    Import rows
                                </Button>
                            </div>
                        </div>

                        {/* Options row */}
                        <div className="flex flex-row flex-wrap items-center justify-between gap-4 border-b border-border-base px-3 py-2">
                            <div className="flex flex-row flex-wrap items-center gap-4">
                                <div className="flex flex-row items-center gap-2">
                                    <Text className="text-base text-content-secondary">Columns separated by:</Text>
                                    <Select
                                        value={separator}
                                        onValueChange={(val) => {
                                            setSeparator(val);
                                            handleReparse(val, hasHeaders);
                                        }}
                                    >
                                        <SelectTrigger className="w-32" loading={false}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SEPARATOR_OPTIONS.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>
                                                    <span>{opt.label}</span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="flex cursor-pointer flex-row items-center gap-2">
                                    <input
                                        id="csv-has-headers-preview"
                                        type="checkbox"
                                        checked={hasHeaders}
                                        onChange={(e) => {
                                            const newValue = e.target.checked;
                                            setHasHeaders(newValue);
                                            handleReparse(separator, newValue);
                                        }}
                                        className="size-4 rounded border-border-base"
                                    />
                                    <label htmlFor="csv-has-headers-preview">
                                        <Text className="text-base text-content-secondary">First row is a header</Text>
                                    </label>
                                </div>
                            </div>
                            <Text className="text-base text-content-secondary">
                                {importState.data.rows.length} rows
                            </Text>
                        </div>

                        {/* Preview table - full width, clickable headers with radio */}
                        <div className="overflow-x-auto">
                            <table className="w-full border-separate border-spacing-0 text-base">
                                <thead>
                                    <tr>
                                        {importState.data.headers.map((header, index) => {
                                            const isSelected = index === selectedColumn;
                                            return (
                                                <th
                                                    key={index}
                                                    onClick={() => setSelectedColumn(index)}
                                                    className={`min-w-[150px] cursor-pointer border-b border-l border-r border-t px-4 py-2 text-left transition-colors ${
                                                        isSelected
                                                            ? "border-border-brand bg-surface-brand/10 font-bold text-content-primary"
                                                            : "border-b-border-base border-l-transparent border-r-transparent border-t-transparent bg-surface-secondary font-medium text-content-secondary hover:bg-surface-01"
                                                    }`}
                                                    data-tracking-id="automations-repository-csv-import-column-clicked"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <span className="flex size-4 shrink-0 items-center justify-center rounded-full border border-content-primary">
                                                            {isSelected && (
                                                                <Circle className="size-2 fill-content-primary text-content-primary" />
                                                            )}
                                                        </span>
                                                        <span className="truncate">{header}</span>
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {importState.data.rows.slice(0, 5).map((row, rowIndex) => {
                                        const isLastRow = rowIndex === Math.min(4, importState.data.rows.length - 1);
                                        return (
                                            <tr key={rowIndex}>
                                                {row.map((cell, cellIndex) => {
                                                    const isSelected = cellIndex === selectedColumn;
                                                    return (
                                                        <td
                                                            key={cellIndex}
                                                            title={cell}
                                                            className={`min-w-[150px] border-b border-l border-r px-4 py-2 text-content-primary ${
                                                                isSelected
                                                                    ? `border-l-border-brand border-r-border-brand bg-surface-brand/5 ${isLastRow ? "border-b-border-brand" : "border-b-border-subtle"}`
                                                                    : "border-b-border-subtle border-l-transparent border-r-transparent"
                                                            }`}
                                                        >
                                                            <span className="line-clamp-1">{cell}</span>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        {importState.data.rows.length > 5 && (
                            <div className="border-t border-border-base px-3 py-2">
                                <Text className="text-xs text-content-muted">
                                    Showing 5 of {importState.data.rows.length} rows
                                </Text>
                            </div>
                        )}
                    </div>
                );

            default:
                // Idle mode - show the URL input with Add button
                return (
                    <div className="flex flex-col gap-1">
                        <div className="flex flex-row items-center gap-2">
                            <Input
                                type="text"
                                value={inputValue}
                                onChange={handleInputChange}
                                onKeyDown={handleKeyDown}
                                placeholder="Enter repository URL (e.g., https://github.com/owner/repo)"
                                className="max-w-none flex-1"
                                disabled={disabled}
                            />
                            <div className="flex">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="md"
                                    onClick={handleAddUrl}
                                    disabled={disabled || !inputValue.trim()}
                                    className="rounded-r-none"
                                    data-tracking-id="add-repo-url"
                                >
                                    Add
                                </Button>
                                <DropdownMenu open={importDropdownOpen} onOpenChange={setImportDropdownOpen}>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            size="md"
                                            disabled={disabled}
                                            className="rounded-l-none border-l border-l-border-base px-2"
                                            data-tracking-id="import-dropdown"
                                        >
                                            {importDropdownOpen ? (
                                                <ChevronUp className="size-4" />
                                            ) : (
                                                <ChevronDown className="size-4" />
                                            )}
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                            onClick={handleImportFromSearch}
                                            data-tracking-id="import-from-search"
                                        >
                                            <span>Import from search</span>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            onClick={handleImportFromCSV}
                                            data-tracking-id="import-from-csv"
                                        >
                                            <span>Import from CSV</span>
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>
                        {validationError && <Text className="text-sm text-content-destructive">{validationError}</Text>}
                    </div>
                );
        }
    };

    return (
        <div className="flex flex-col gap-3">
            {fileInput}
            {renderInputArea()}

            {repoUrls.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-border-base">
                    <div className="flex flex-row items-center justify-between border-b border-border-base bg-surface-base px-3 py-1">
                        <Text className="text-sm text-content-secondary">
                            {repoUrls.length} / {MAX_REPO_URLS} repositories
                        </Text>
                        <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            onClick={onClearAll}
                            disabled={disabled}
                            data-tracking-id="clear-all-repo-urls"
                        >
                            Clear
                        </Button>
                    </div>
                    <div className="flex max-h-48 flex-col overflow-y-auto">
                        {repoUrls.map((url, index) => {
                            const repoName = url.split("/").slice(-2).join("/");
                            return (
                                <div
                                    key={url}
                                    className={`flex flex-row items-center justify-between px-3 py-2 ${index < repoUrls.length - 1 ? "border-b border-border-subtle" : ""}`}
                                >
                                    <div className="flex min-w-0 flex-1 flex-col">
                                        <Text className="truncate text-base font-medium">{repoName}</Text>
                                        <Text className="truncate text-sm text-content-secondary">{url}</Text>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="xs"
                                        disabled={disabled}
                                        onClick={() => onRemoveUrl(url)}
                                        LeadingIcon={IconClose}
                                        className="shrink-0"
                                        data-tracking-id="remove-repo-url"
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const RepoFilteringContent: FC<{
    repoSearchString: string;
    runnerId: string;
    disabled: boolean;
    scmHost: string;
    onScmHostChange: (scmHost: string) => void;
    onAddFilter: (filter: string) => void;
    onClearFilter: () => void;
    onSearchComplete?: (repos: { url: string }[]) => void;
}> = memo(
    ({
        repoSearchString,
        runnerId,
        scmHost: selectedScmHost,
        onAddFilter,
        onClearFilter,
        onScmHostChange,
        onSearchComplete,
    }) => {
        const {
            data: scmIntegrations,
            isLoading: isLoadingSCMIntegrations,
            error: listSCMIntegrationsError,
            refetch: refetchSCMIntegrations,
        } = useListRunnerSCMIntegrations(runnerId);

        let scmHost: string;
        let scmId: string;
        if (scmIntegrations && scmIntegrations.length > 0) {
            // find the SCM integration that matches the selected SCM host
            const selected = scmIntegrations.find((it) => it.host === selectedScmHost);
            if (selected) {
                scmHost = selected.host;
                scmId = selected.scmId;
            } else {
                // if the selected SCM host is not found in the list of SCM integrations, use the first integration
                scmHost = scmIntegrations[0].host;
                scmId = scmIntegrations[0].scmId;
            }
        } else {
            scmHost = "";
            scmId = "";
        }

        const {
            data: repositoriesData,
            isLoading: isSearching,
            error: searchRepositoriesError,
            refetch: refetchRepositories,
            fetchNextPage,
            hasNextPage,
            isFetchingNextPage,
        } = useSearchRepositoriesPaginated({
            runnerId,
            query: repoSearchString,
            scmHost,
            enabled: scmHost !== "" && repoSearchString !== "",
            searchMode: SearchMode.NATIVE,
        });

        // Flatten paginated results and extract total count from first page
        const repositories = useMemo(
            () => repositoriesData?.pages.flatMap((page) => page.repositories) ?? [],
            [repositoriesData],
        );
        const totalCount = repositoriesData?.pages[0]?.totalCount ?? -1;

        useEffect(() => {
            if (scmIntegrations && scmIntegrations.length > 0 && !selectedScmHost) {
                // if the selected SCM host is not set, use the first scm integration found for the runner
                onScmHostChange(scmIntegrations[0].host);
            }
        }, [scmIntegrations, onScmHostChange, selectedScmHost]);

        // Notify parent when search results change
        useEffect(() => {
            if (onSearchComplete && repositories && repositories.length > 0) {
                onSearchComplete(repositories.map((r) => ({ url: r.url })));
            }
        }, [repositories, onSearchComplete]);

        if (isLoadingSCMIntegrations) {
            return (
                <Text className="animate-pulse text-base text-content-secondary">
                    Finding suitable repository provider…
                </Text>
            );
        }

        if (listSCMIntegrationsError) {
            return (
                <div className="flex flex-row items-baseline gap-2">
                    <ErrorMessage error={listSCMIntegrationsError} />
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            void refetchSCMIntegrations();
                        }}
                        data-tracking-id="retry"
                    >
                        Retry
                    </Button>
                </div>
            );
        }

        if (isSearching) {
            return <Text className="animate-pulse text-base text-content-secondary">Searching repositories…</Text>;
        }

        if (repositories.length > 0) {
            return (
                <RepoFilterResult
                    searchString={repoSearchString}
                    repositories={repositories}
                    totalCount={totalCount}
                    onClear={onClearFilter}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    onLoadMore={() => void fetchNextPage()}
                />
            );
        }

        if (scmIntegrations && scmIntegrations.length === 0) {
            return (
                <Text>The selected runner does not support repository trigger. Please select a different runner.</Text>
            );
        }

        return (
            <RepoFilterInput
                initialSearchString={repoSearchString}
                loading={isSearching}
                onAddFilter={onAddFilter}
                error={searchRepositoriesError}
                onRetry={refetchRepositories}
                scmId={scmId}
            />
        );
    },
);
RepoFilteringContent.displayName = "RepoFilteringContent";

const RepoFilterInput: FC<{
    initialSearchString: string;
    loading: boolean;
    onAddFilter: (filter: string) => void;
    error?: Error | null;
    onRetry?: () => void;
    scmId?: string;
}> = memo(({ initialSearchString, loading, onAddFilter, error, onRetry, scmId }) => {
    const [inputValue, setInputValue] = useState(initialSearchString);
    const [showError, setShowError] = useState(!!error);

    useEffect(() => {
        setShowError(!!error);
    }, [error]);

    const submitSearchString = () => {
        const value = inputValue.trim();
        if (!value) return;
        if (error && value === initialSearchString && onRetry) {
            // Same search string and there was an error - retry the query
            onRetry();
        } else {
            onAddFilter(value);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            submitSearchString();
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
        if (showError) {
            setShowError(false);
        }
    };

    const scmDisplayName = scmId ? getScmDisplayName(scmId) : "repository";
    const placeholderText =
        scmDisplayName === "repository"
            ? "Describe what repositories to include or paste a filter"
            : `Describe what repositories to include or paste a ${scmDisplayName} filter`;
    const hasSearchableContent = inputValue.trim().length > 0;

    return (
        <div className="flex w-full flex-col gap-2">
            <div className="flex w-full flex-row items-center gap-2">
                <Input
                    value={inputValue}
                    type="text"
                    placeholder={placeholderText}
                    className="h-9 max-w-none flex-1 bg-transparent text-content-primary focus:outline-none focus:ring-0"
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                />
                <Button
                    type="button"
                    variant="primary"
                    size="md"
                    className="aspect-square p-0"
                    loading={loading}
                    disabled={!hasSearchableContent}
                    LeadingIcon={withGitpodSize(CornerDownLeft)}
                    onClick={submitSearchString}
                    data-tracking-id="submitsearchstring"
                >
                    <span className="sr-only">Add repository search filter</span>
                </Button>
            </div>
            {showError && error && <ErrorMessage error={error} />}
        </div>
    );
});
RepoFilterInput.displayName = "RepoFilterInput";

const RepoFilterResult: FC<{
    searchString: string;
    repositories: PlainSearchRepository[];
    totalCount: number;
    onClear: () => void;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
    onLoadMore?: () => void;
}> = memo(({ searchString, repositories, totalCount, onClear, hasNextPage, isFetchingNextPage, onLoadMore }) => {
    // Format the count text based on whether we know the total
    const countText = useMemo(() => {
        if (totalCount > 0) {
            return `${repositories.length} of ${totalCount} matching repositories`;
        }
        if (hasNextPage) {
            // Total unknown but more pages exist
            return `${repositories.length}+ matching repositories`;
        }
        return `${repositories.length} matching repositories`;
    }, [repositories.length, totalCount, hasNextPage]);

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-row items-center gap-2">
                <Pill variant="brand" size="md">
                    {searchString}
                </Pill>
                <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    onClick={onClear}
                    data-tracking-id="code-context-repo-search-clear"
                >
                    Clear
                </Button>
            </div>
            {/* Read-only preview of matching repositories */}
            <div className="flex flex-col gap-2">
                <Text className="text-sm text-content-secondary">{countText}</Text>
                <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                    {repositories.map((repo) => (
                        <div
                            key={repo.url}
                            className="flex flex-row items-center gap-2 rounded bg-surface-secondary px-2 py-1"
                        >
                            <Text className="flex-1 truncate text-sm">{repo.url}</Text>
                        </div>
                    ))}
                    {hasNextPage && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={onLoadMore}
                            disabled={isFetchingNextPage}
                            className="mt-2"
                            data-tracking-id="code-context-repo-search-load-more"
                        >
                            {isFetchingNextPage ? "Loading..." : "Load more"}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
});
RepoFilterResult.displayName = "RepoFilterResult";
